import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import {
  hasJwtInSessionStorage,
  terminateSessionAndGoToLogin,
} from "../Auth/sessionTermination";
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from "../Auth/tokenKeys";
import { PATH } from "../PATH";

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (v: unknown) => void;
  reject: (e: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(token);
  });
  failedQueue = [];
}

function isAuthTokenRequestUrl(url: string): boolean {
  return url.includes("/token/refresh/") || url.endsWith("/token/");
}

export const apiClient = axios.create({
  baseURL: PATH.baseApi || "",
});

apiClient.interceptors.request.use((config) => {
  const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    if (!originalRequest) {
      return Promise.reject(error);
    }

    const reqUrl = originalRequest.url || "";
    const status = error.response?.status;

    if (
      status === 403 &&
      hasJwtInSessionStorage() &&
      !isAuthTokenRequestUrl(reqUrl)
    ) {
      terminateSessionAndGoToLogin();
      return Promise.reject(error);
    }

    if (status !== 401) {
      return Promise.reject(error);
    }

    if (isAuthTokenRequestUrl(reqUrl)) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      terminateSessionAndGoToLogin();
      return Promise.reject(error);
    }

    const refresh = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refresh) {
      terminateSessionAndGoToLogin();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers = originalRequest.headers || {};
          (originalRequest.headers as Record<string, string>).Authorization =
            `Bearer ${token as string}`;
          return apiClient(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post<{ access: string }>(
        `${PATH.baseApi}/token/refresh/`,
        { refresh }
      );
      const newAccess = data.access;
      sessionStorage.setItem(ACCESS_TOKEN_KEY, newAccess);
      processQueue(null, newAccess);
      originalRequest.headers = originalRequest.headers || {};
      (originalRequest.headers as Record<string, string>).Authorization =
        `Bearer ${newAccess}`;
      return apiClient(originalRequest);
    } catch (refreshErr) {
      processQueue(refreshErr, null);
      terminateSessionAndGoToLogin();
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);
